import moment from "moment";

/**
 * Get active closures from hotel data filtered by order type.
 * - "ONLINE_ORDER" or "ALL" closures apply to both PICKUP and DELIVERY
 * - "PICKUP" closures only apply when orderType is PICKUP
 * - "DELIVERY" closures only apply when orderType is DELIVERY
 */
export function getActiveClosures(hotel, orderType) {
  if (
    !hotel?.upcomming_scheduled_disable_data ||
    !Array.isArray(hotel.upcomming_scheduled_disable_data)
  ) {
    return [];
  }
  return hotel.upcomming_scheduled_disable_data.filter((c) => {
    if (c.domain_type === "ONLINE_ORDER" || c.domain_type === "ALL") {
      return true;
    }
    if (c.domain_type === "PICKUP" && orderType === "PICKUP") {
      return true;
    }
    if (c.domain_type === "DELIVERY" && orderType === "DELIVERY") {
      return true;
    }
    return false;
  });
}

/**
 * Check if a given epoch timestamp falls within any closure window
 */
export function isTimeInClosure(epochMs, closures) {
  if (!closures || !closures.length) return false;
  return closures.some(
    (c) => epochMs >= c.schedule_start_time && epochMs < c.schedule_end_time
  );
}

/**
 * Filter time slots by removing any that fall within a closure window.
 * Applied AFTER normal slot generation as a final filter.
 */
export function filterSlotsByClosures(slots, selectedDate, closures) {
  if (!closures.length || !slots.length) {
    return slots;
  }

  const dateStr = moment(selectedDate).format("YYYY-MM-DD");

  // Quick check: does any closure overlap with this date at all?
  const dayStart = moment(dateStr, "YYYY-MM-DD").startOf("day").valueOf();
  const dayEnd = moment(dateStr, "YYYY-MM-DD").endOf("day").valueOf();
  const hasOverlap = closures.some(
    (c) => c.schedule_start_time < dayEnd && c.schedule_end_time > dayStart
  );
  if (!hasOverlap) {
    return slots;
  }

  return slots.filter((slot) => {
    const slotEpoch = moment(
      `${dateStr} ${slot.value}`,
      "YYYY-MM-DD hh:mm a"
    ).valueOf();
    // Inclusive on both ends. A slot is a service-time commitment, not an
    // instantaneous moment — a slot that lands exactly on a closure end
    // (e.g. 11:59 PM slot vs a 10:45–11:59 PM closure) overlaps the
    // closed window and must be dropped, otherwise we'd show a single
    // "11:59 PM" today slot while every other slot in the evening is
    // suppressed. `isTimeInClosure` keeps the half-open semantics it
    // needs for "is the current moment in closure" checks elsewhere.
    return !closures.some(
      (c) =>
        typeof c?.schedule_start_time === "number" &&
        typeof c?.schedule_end_time === "number" &&
        slotEpoch >= c.schedule_start_time &&
        slotEpoch <= c.schedule_end_time
    );
  });
}
